import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Scan, Link as LinkIcon, X, Loader2, FileText, ChevronDown, ChevronUp, Utensils, Flame, Beef, Wheat, Droplets } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const RecipeScanner = ({ onClose, onSaveToLog }) => {
  const [mode, setMode] = useState('url'); // 'url' or 'text'
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showIngredients, setShowIngredients] = useState(false);

  const handleAnalyze = async () => {
    if (mode === 'url' && !url.trim()) {
      toast.error('Please enter a recipe URL');
      return;
    }
    if (mode === 'text' && !text.trim()) {
      toast.error('Please paste recipe text');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const payload = mode === 'url' ? { url: url.trim() } : { text: text.trim() };
      const response = await axios.post(`${API}/nutrition/recipe/analyze`, payload, { timeout: 90000 });
      setResult(response.data);
      toast.success('Recipe analyzed!');
    } catch (error) {
      console.error('Recipe analyze error:', error);
      let detail;
      if (error.code === 'ECONNABORTED') {
        detail = 'Request timed out. The AI is busy — please try again.';
      } else if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      } else if (error.response?.status) {
        detail = `Server error (${error.response.status}). Please try again in a moment.`;
      } else {
        detail = 'Network error. Please check your connection and try again.';
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleLogRecipe = () => {
    if (!result || !onSaveToLog) return;
    const n = result.nutrition_per_serving || {};
    onSaveToLog({
      food_name: result.recipe_name,
      calories: Math.round(n.calories || 0),
      protein: Math.round(n.protein || 0),
      carbs: Math.round(n.carbs || 0),
      fats: Math.round(n.fats || 0),
      fiber: Math.round(n.fiber || 0),
      sugar: Math.round(n.sugar || 0),
      servings: 1,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="recipe-scanner-modal">
      <Card className="glass w-full max-w-lg max-h-[90vh] overflow-y-auto border border-stone-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-blood-400" />
            <h2 className="text-xl font-bold text-white">Recipe Scanner</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-white transition-colors" data-testid="recipe-scanner-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <>
            {/* Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setMode('url')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  mode === 'url' ? 'bg-blood-600 text-white' : 'bg-stone-800 text-stone-400 hover:text-white'
                }`}
                data-testid="recipe-mode-url"
              >
                <LinkIcon className="w-4 h-4" />
                Paste URL
              </button>
              <button
                onClick={() => setMode('text')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  mode === 'text' ? 'bg-blood-600 text-white' : 'bg-stone-800 text-stone-400 hover:text-white'
                }`}
                data-testid="recipe-mode-text"
              >
                <FileText className="w-4 h-4" />
                Paste Recipe
              </button>
            </div>

            {/* Input */}
            {mode === 'url' ? (
              <div className="mb-4">
                <p className="text-stone-500 text-xs mb-2">Paste a recipe URL from any site (AllRecipes, Food Network, etc.)</p>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.allrecipes.com/recipe/..."
                  className="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blood-700"
                  data-testid="recipe-url-input"
                />
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-stone-500 text-xs mb-2">Paste ingredients and instructions from a cookbook, screenshot, or website</p>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={"Chicken Stir Fry\n\nIngredients:\n- 2 chicken breasts\n- 1 cup broccoli\n- 2 tbsp soy sauce\n- 1 tbsp sesame oil\n..."}
                  rows={8}
                  className="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blood-700 resize-none"
                  data-testid="recipe-text-input"
                />
              </div>
            )}

            {/* Analyze Button */}
            <Button
              onClick={handleAnalyze}
              disabled={loading}
              className="w-full bg-blood-600 hover:bg-blood-700 font-bold py-3"
              data-testid="recipe-analyze-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing Recipe...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Scan className="w-4 h-4" />
                  Analyze Recipe
                </span>
              )}
            </Button>
          </>
        ) : (
          /* Results View */
          <div className="space-y-4">
            {/* Recipe Name */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-blood-950/50 border border-blood-800/30 flex items-center justify-center mx-auto mb-3">
                <Utensils className="w-6 h-6 text-blood-500" />
              </div>
              <h3 className="text-lg font-bold text-white" data-testid="recipe-result-name">{result.recipe_name}</h3>
              <p className="text-stone-500 text-sm">{result.servings} serving{result.servings !== 1 ? 's' : ''}</p>
            </div>

            {/* Per Serving Nutrition */}
            <div className="p-4 rounded-xl bg-stone-900/70 border border-stone-800">
              <p className="text-stone-500 text-xs uppercase tracking-wider mb-3 text-center">Per Serving</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Flame className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-white font-bold text-lg">{Math.round(result.nutrition_per_serving?.calories || 0)}</span>
                  </div>
                  <span className="text-stone-500 text-xs">Calories</span>
                </div>
                <div>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Beef className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-white font-bold text-lg">{Math.round(result.nutrition_per_serving?.protein || 0)}g</span>
                  </div>
                  <span className="text-stone-500 text-xs">Protein</span>
                </div>
                <div>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Wheat className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-white font-bold text-lg">{Math.round(result.nutrition_per_serving?.carbs || 0)}g</span>
                  </div>
                  <span className="text-stone-500 text-xs">Carbs</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center mt-3">
                <div>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Droplets className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="text-white font-bold">{Math.round(result.nutrition_per_serving?.fats || 0)}g</span>
                  </div>
                  <span className="text-stone-500 text-xs">Fats</span>
                </div>
                <div>
                  <span className="text-white font-bold">{Math.round(result.nutrition_per_serving?.fiber || 0)}g</span>
                  <br /><span className="text-stone-500 text-xs">Fiber</span>
                </div>
                <div>
                  <span className="text-white font-bold">{Math.round(result.nutrition_per_serving?.sugar || 0)}g</span>
                  <br /><span className="text-stone-500 text-xs">Sugar</span>
                </div>
              </div>
            </div>

            {/* Ingredients (collapsible) */}
            {result.ingredients?.length > 0 && (
              <div className="rounded-xl border border-stone-800 overflow-hidden">
                <button
                  onClick={() => setShowIngredients(!showIngredients)}
                  className="w-full flex items-center justify-between p-3 text-stone-400 hover:text-white transition-colors"
                  data-testid="recipe-toggle-ingredients"
                >
                  <span className="text-sm font-semibold">{result.ingredients.length} Ingredients</span>
                  {showIngredients ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showIngredients && (
                  <div className="px-3 pb-3 space-y-1.5">
                    {result.ingredients.map((ing, i) => (
                      <div key={i} className="text-sm text-stone-400 flex gap-2">
                        <span className="text-blood-500 font-bold shrink-0">-</span>
                        <span>{ing.amount ? `${ing.amount} ${ing.name}` : ing.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setResult(null)}
                variant="outline"
                className="flex-1 border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 hover:text-white"
                data-testid="recipe-scan-another"
              >
                Scan Another
              </Button>
              <Button
                onClick={handleLogRecipe}
                className="flex-1 bg-blood-600 hover:bg-blood-700 font-bold"
                data-testid="recipe-log-serving"
              >
                Log 1 Serving
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RecipeScanner;
